// Slice A5 — minimal toast provider. Usage:
//   const toast = useToast(); toast.success("Saved"); toast.error("Bad");
import React, { createContext, useCallback, useContext, useState } from "react";
import { CheckCircle2, XCircle } from "lucide-react";

const ToastCtx = createContext(null);

let idCounter = 0;

export function ToastProvider({ children }) {
  const [items, setItems] = useState([]);

  const remove = useCallback((id) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (type, msg) => {
      const id = ++idCounter;
      setItems((prev) => [...prev, { id, type, msg }]);
      setTimeout(() => remove(id), 3000);
    },
    [remove],
  );

  const value = {
    success: (msg) => push("success", msg),
    error: (msg) => push("error", msg),
  };

  return (
    <ToastCtx.Provider value={value}>
      {children}
      <div className="fixed bottom-6 right-6 z-[60] flex flex-col gap-2 pointer-events-none">
        {items.map((t) => (
          <div
            key={t.id}
            data-testid={`toast-${t.type}`}
            className={`pointer-events-auto flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border backdrop-blur-xl ${
              t.type === "success"
                ? "bg-green-50/90 text-green-800 border-green-200"
                : "bg-red-50/90 text-red-800 border-red-200"
            }`}
            style={{ boxShadow: "0 10px 40px rgba(124,92,255,0.12), 0 2px 8px rgba(11,15,26,0.06)" }}
          >
            {t.type === "success" ? (
              <CheckCircle2 className="w-4 h-4 text-green-600" />
            ) : (
              <XCircle className="w-4 h-4 text-red-600" />
            )}
            <span>{t.msg}</span>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const v = useContext(ToastCtx);
  if (!v) throw new Error("useToast must be used inside <ToastProvider>");
  return v;
}
